import {
  createOrderFromResponse,
  createResponseFromOrder
} from '../order/helpers'
import {
  GridDefinition,
  Security,
  SecurityOrderData,
  SecurityStaticData
} from './reducer'
import { OrderResponse } from '../order/types'
import { CountryCodeToRegion, defaultFilter, Filter } from '../filter/types'
import dayjs from 'dayjs'
import { GetNestedType, getNestedValue, NestedKeyOf } from '../../helpers/utils'

export const SECURITIES_BUFFER_DELAY = 250
export const SILENT_PAGE_SIZE = 500

export const DYNAMIC_WATCHLISTS = ['IG Most Active', 'HY Most Active']

type DateToString<T> = {
  [P in keyof T]: T[P] extends Date
    ? string
    : T[P] extends Date | undefined
    ? string | undefined
    : T[P]
}

export type SecurityResponse = DateToString<
  Omit<Security, 'bestBid' | 'bestOffer'> & {
    bestBid?: DateToString<OrderResponse>
    bestOffer?: DateToString<OrderResponse>
  }
>

export const createSecurityFromResponse = (
  securityResponse: SecurityResponse
): Security => ({
  ...securityResponse,
  maturityDate: new Date(securityResponse.maturityDate),
  issueDate: new Date(securityResponse.issueDate),
  settlementDate: new Date(securityResponse.settlementDate),
  datedDate: new Date(securityResponse.datedDate),
  benchmarkMaturity: securityResponse.benchmarkMaturity
    ? new Date(securityResponse.benchmarkMaturity)
    : undefined,
  firstCouponDate: securityResponse.firstCouponDate
    ? new Date(securityResponse.firstCouponDate)
    : undefined,
  nextCouponDate: securityResponse.nextCouponDate
    ? new Date(securityResponse.nextCouponDate)
    : undefined,
  bestBid:
    securityResponse.bestBid &&
    createOrderFromResponse(securityResponse.bestBid),
  bestOffer:
    securityResponse.bestOffer &&
    createOrderFromResponse(securityResponse.bestOffer)
})

export const createResponseFromSecurity = (
  security: Security
): SecurityResponse => ({
  ...security,
  datedDate: security.datedDate?.toISOString() || '',
  issueDate: security.issueDate?.toISOString() || '',
  settlementDate: security.settlementDate?.toISOString() || '',
  maturityDate: security.maturityDate.toISOString(),
  benchmarkMaturity: security.benchmarkMaturity?.toISOString(),
  firstCouponDate: security.firstCouponDate?.toISOString(),
  nextCouponDate: security.nextCouponDate?.toISOString(),
  bestBid: security.bestBid && createResponseFromOrder(security.bestBid),
  bestOffer: security.bestOffer && createResponseFromOrder(security.bestOffer)
})

const fromEntries = <T>(entries: Array<[keyof T, T[keyof T]]>): T =>
  entries.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as T)

export const insertGridDef = (
  gridDefinitions: { [gridIndex: number]: GridDefinition },
  newGridDef: Omit<GridDefinition, 'order'>,
  afterIndex: number
): { [gridIndex: number]: GridDefinition } => {
  const entries = Object.entries(gridDefinitions).map<[number, GridDefinition]>(
    ([gridIndex, gridDef]) => [Number(gridIndex), gridDef]
  )
  const gridDefsBefore = entries.filter(
    ([, gridDef]) => gridDef.order <= gridDefinitions[afterIndex].order
  )
  const gridDefsAfter = entries
    .filter(([, gridDef]) => gridDef.order > gridDefinitions[afterIndex].order)
    .map<[number, GridDefinition]>(([gridIndex, gridDef]) => [
      gridIndex,
      { ...gridDef, order: gridDef.order + 1 }
    ])
  const newIndex = entries.length
    ? Math.max(...entries.map(([gridIndex]) => gridIndex)) + 1
    : 0
  return {
    ...fromEntries(gridDefsBefore),
    [newIndex]: {
      ...newGridDef,
      order: (gridDefinitions[afterIndex]?.order ?? 0) + 1
    },
    ...fromEntries(gridDefsAfter)
  }
}

export const removeGridDef = <T>(
  gridDefinitions: { [gridIndex: number]: T },
  gridIndexToRemove: number
): { [gridIndex: number]: T } => {
  const entries = Object.entries(gridDefinitions)
    .map<[number, T]>(([gridIndex, gridDef]) => [Number(gridIndex), gridDef])
    .filter(([gridIndex]) => gridIndex !== gridIndexToRemove)
  return fromEntries(entries)
}

export const ReduceSecurityStaticData = (
  oldRecord: Record<number, SecurityStaticData>,
  newData: Security[]
) => {
  let update = false
  const newRecord = { ...oldRecord }
  newData.forEach((sec) => {
    const securityStaticData = SecurityStaticDataFromSecurity(sec)
    if (
      !newRecord.hasOwnProperty(sec.id) ||
      !SecurityStaticDataEqual(newRecord[sec.id], securityStaticData)
    ) {
      update = true
      newRecord[sec.id] = securityStaticData
    }
  })
  return update ? newRecord : oldRecord
}

const SecurityStaticDataFromSecurity = (sec: Security): SecurityStaticData => {
  return {
    id: sec.id,
    isRestricted: sec.isRestricted,
    issuerSymbol: sec.issuerSymbol,
    cusip: sec.cusip,
    isin: sec.isin,
    coupon: sec.coupon,
    couponType: sec.couponType,
    maturityDate: sec.maturityDate,
    currency: sec.currency,
    dayCount: sec.dayCount,
    debtType: sec.debtType,
    minimumSize: sec.minimumSize,
    minimumSizeIncrement: sec.minimumSizeIncrement,
    defaultOrderSize: sec.defaultOrderSize,
    minimumPriceIncrement: sec.minimumPriceIncrement,
    benchmarkName: sec.benchmarkName,
    bidBenchmarkPrice: sec.bidBenchmarkPrice,
    offerBenchmarkPrice: sec.offerBenchmarkPrice,
    amountIssued: sec.amountIssued,
    amountOutstanding: sec.amountOutstanding,
    datedDate: sec.datedDate,
    benchmarkISIN: sec.benchmarkISIN,
    benchmarkCusip: sec.benchmarkCusip,
    benchmarkSecurity: sec.benchmarkSecurity,
    isBenchmark: sec.isBenchmark,
    benchmarkMaturity: sec.benchmarkMaturity,
    benchmarkCoupon: sec.benchmarkCoupon,
    country: sec.country,
    firstCouponDate: sec.firstCouponDate,
    nextCouponDate: sec.nextCouponDate,
    issueDate: sec.issueDate,
    markupRate: sec.markupRate,
    payFreq: sec.payFreq,
    product: sec.product,
    settlementDate: sec.settlementDate,
    sAndPRating: sec.sAndPRating,
    sector: sec.sector,
    termToMat: sec.termToMat,
    minimumPiece: sec.minimumPiece,
    description: sec.description,
    boardLabel: sec.boardLabel,
    series: sec.series,
    issuerName: sec.issuerName,
    marketVolume: sec.marketVolume,
    internalVolume: sec.internalVolume
  }
}

export const securityOrderDataFromSecurity = (
  security: Security
): SecurityOrderData => {
  const {
    id,
    isRestricted,
    bestBid,
    bestOffer,
    marketVolume,
    isBenchmark,
    eligibleRfqBidCount,
    eligibleRfqOfferCount,
    bestRfqBid,
    bestRfqOffer
  } = security
  return {
    id,
    isRestricted,
    bestBid,
    bestOffer,
    marketVolume,
    isBenchmark,
    eligibleRfqBidCount,
    eligibleRfqOfferCount,
    bestRfqBid,
    bestRfqOffer
  }
}

/* Tolerate dates that have crossed a JSON boundary as strings (OpenFin windows
   serialize state), so the cache equality check can't throw on .getTime(). */
const sameTime = (a?: Date | string | null, b?: Date | string | null) => {
  const ta = a == null ? undefined : new Date(a).getTime()
  const tb = b == null ? undefined : new Date(b).getTime()
  return ta === tb
}

const SecurityStaticDataEqual = (
  s1: SecurityStaticData,
  s2: SecurityStaticData
) => {
  return (
    s1.id === s2.id &&
    s1.issuerSymbol === s2.issuerSymbol &&
    s1.cusip === s2.cusip &&
    s1.isin === s2.isin &&
    s1.coupon === s2.coupon &&
    s1.couponType === s2.couponType &&
    sameTime(s1.maturityDate, s2.maturityDate) &&
    s1.currency === s2.currency &&
    s1.dayCount === s2.dayCount &&
    s1.debtType === s2.debtType &&
    s1.minimumSize === s2.minimumSize &&
    s1.minimumSizeIncrement === s2.minimumSizeIncrement &&
    s1.defaultOrderSize === s2.defaultOrderSize &&
    s1.minimumPriceIncrement === s2.minimumPriceIncrement &&
    s1.benchmarkName === s2.benchmarkName &&
    s1.amountIssued === s2.amountIssued &&
    s1.amountOutstanding === s2.amountOutstanding &&
    sameTime(s1.datedDate, s2.datedDate) &&
    s1.benchmarkISIN === s2.benchmarkISIN &&
    s1.benchmarkCusip === s2.benchmarkCusip &&
    s1.benchmarkSecurity === s2.benchmarkSecurity &&
    s1.isBenchmark === s2.isBenchmark &&
    sameTime(s1.benchmarkMaturity, s2.benchmarkMaturity) &&
    s1.benchmarkCoupon === s2.benchmarkCoupon &&
    s1.country === s2.country &&
    sameTime(s1.firstCouponDate, s2.firstCouponDate) &&
    sameTime(s1.nextCouponDate, s2.nextCouponDate) &&
    sameTime(s1.issueDate, s2.issueDate) &&
    s1.markupRate === s2.markupRate &&
    s1.payFreq === s2.payFreq &&
    s1.product === s2.product &&
    sameTime(s1.settlementDate, s2.settlementDate) &&
    s1.sAndPRating === s2.sAndPRating &&
    s1.sector === s2.sector &&
    s1.termToMat === s2.termToMat &&
    s1.minimumPiece === s2.minimumPiece &&
    s1.description === s2.description &&
    s1.boardLabel === s2.boardLabel &&
    s1.series === s2.series &&
    s1.issuerName === s2.issuerName
    // &&
    // s1.marketVolume === s2.marketVolume &&
    // s1.internalVolume === s2.internalVolume
  )
}
export const getDefaultSecurityFilter = () => ({
  InWatchlistId: undefined,
  IsMine: undefined,
  IssuerSymbol: undefined,
  MyBook: undefined,
  MyFirm: undefined,
  Query: undefined,
  QuoteReliability: undefined,
  SecurityId: undefined,
  filter: defaultFilter,
  isTicker: false,
  showLive: undefined,
  size: undefined,
  sortToTop: [],
  tickerAndMaturity: false
})
export const getTickerAndMaturity = (query?: string) => {
  if (!query) {
    return { isTicker: false, tickerAndMaturity: false }
  }
  const tickerRegex = /^\s*[a-zA-Z]+\s*$/gm
  const tickerMaturityRegex = /^[a-zA-Z]+ [2-9][2-9]\s*$/gm
  return {
    isTicker: tickerRegex.test(query),
    tickerAndMaturity: tickerMaturityRegex.test(query)
  }
}
export const createSecurityFilter = (
  definition?: GridDefinition,
  SecurityId?: number[]
) => {
  const defaultSecurityFilter = getDefaultSecurityFilter()
  if (!definition) {
    return defaultSecurityFilter
  }
  const andSecurities = {
    SecurityId
  }
  const {
    watchlistId: InWatchlistId,
    issuerFilter: IssuerSymbol,
    isMine: IsMine,
    queryFilter: Query,
    showLive,
    showRfqEligible: ShowRFQEligible,
    quoteReliability,
    myFirm: MyFirm,
    myBook: MyBook,
    filter: advancedFilter,
    size,
    securitiesFilter,
    sortToTop = []
  } = definition

  if (securitiesFilter?.length) {
    return securitiesFilter
  }

  if (Query) {
    const tickerInfo = getTickerAndMaturity(Query)
    return {
      ...defaultSecurityFilter,
      Query,
      ...tickerInfo,
      ...(tickerInfo.isTicker || tickerInfo.tickerAndMaturity
        ? { filter: advancedFilter }
        : {}),
      showLive
    }
  }

  return {
    ...defaultSecurityFilter,
    InWatchlistId,
    IssuerSymbol,
    IsMine,
    Query,
    showLive,
    ShowRFQEligible,
    QuoteReliability: quoteReliability,
    MyFirm,
    MyBook,
    filter: advancedFilter,
    size,
    sortToTop,
    ...getTickerAndMaturity(IssuerSymbol),
    ...andSecurities
  }
}
type SecurityExportColumn<
  P extends NestedKeyOf<Security> = NestedKeyOf<Security>
> = {
  header: string
  property: P
  // TODO: this does not properly enforce the constraint
  formatter?: (value: GetNestedType<Security, P>) => string | undefined
}
export const updateFormatter = (value?: Date) =>
  value ? dayjs(value).format('MM-DD-YYYY HH:mm:ss') : ''
export const decimalFormatter = (value?: number) =>
  value !== undefined ? value.toFixed(4) : ''

const synonyms: Record<string, string> = {
  USA: 'US',
  CAN: 'CA',
  XB: 'NL' // Belgium, the Netherlands, Luxembourg
}
const regionFormatter = (countryCode?: string) => {
  const lookup = synonyms[countryCode || ''] || countryCode
  return lookup ? CountryCodeToRegion[lookup] || '' : ''
}
const defaultFormatter = (value: unknown) => {
  if (typeof value === 'object' || value === null || value === undefined) {
    return ''
  }
  const strValue = String(value).replace(/"/g, '""')
  return `"${strValue}"`
}
const exportColumns = [
  {
    header: 'Security',
    property: 'boardLabel'
  },
  {
    header: 'Cusip',
    property: 'cusip'
  },
  {
    header: 'ISIN',
    property: 'isin'
  },
  {
    header: 'BidUpdateTime',
    property: 'bestBid.updateTime',
    formatter: updateFormatter
  },
  {
    header: 'BidProvider',
    property: 'bestBid.displayName'
  },
  {
    header: 'BidSize',
    property: 'bestBid.size'
  },
  {
    header: 'Bid YTW',
    property: 'bestBid.ytw'
  },
  {
    header: 'BidSpread',
    property: 'bestBid.spread'
  },
  {
    header: 'Bid',
    property: 'bestBid.price'
  },
  {
    header: 'Ofr',
    property: 'bestOffer.price'
  },
  {
    header: 'OfrSpread',
    property: 'bestOffer.spread'
  },
  {
    header: 'Ofr YTW',
    property: 'bestOffer.ytw'
  },
  {
    header: 'OfrSize',
    property: 'bestOffer.size'
  },
  {
    header: 'OfrProvider',
    property: 'bestOffer.displayName'
  },
  {
    header: 'OfrUpdateTime',
    property: 'bestOffer.updateTime',
    formatter: updateFormatter
  },
  {
    header: 'Benchmark',
    property: 'benchmarkName'
  },
  {
    header: 'Benchmark Cusip',
    property: 'benchmarkCusip'
  },
  {
    header: 'Sector',
    property: 'sector'
  },
  {
    header: 'Series',
    property: 'series'
  },
  {
    header: 'S&P Rating',
    property: 'sAndPRating'
  },
  {
    header: 'Region',
    property: 'country',
    formatter: regionFormatter
  }
  // TODO: Add these back in when we have them
  /* "BidVenueID", "OfrVenueID", "BidCustomer", "OfrCustomer" */
] as const satisfies readonly SecurityExportColumn[]

const getExportColumns = (includeMakerData: boolean) => {
  const columns = includeMakerData
    ? exportColumns
    : exportColumns.filter((column) => !column.header.includes('Provider'))
  return columns as readonly SecurityExportColumn[]
}
const securityToArray = (security: Security, includeMakerData: boolean) => {
  return getExportColumns(includeMakerData).map(
    ({ property, formatter = defaultFormatter }) => {
      return formatter(
        getNestedValue(security, property as NestedKeyOf<Security>)
      )
    }
  )
}
export const compileCsvData = (
  securities: Security[],
  includeMakerData: boolean
) => {
  const headers = getExportColumns(includeMakerData).map(
    (column) => column.header
  )
  return [
    headers.join(','),
    ...securities.map((security) =>
      securityToArray(security, includeMakerData).join(',')
    )
  ].join('\r\n')
}

export interface GetSecuritiesFilter {
  InWatchlistId?: number
  NotInWatchlistId?: number
  IssuerSymbol?: string
  IsMine?: boolean
  MyBook?: boolean
  MyFirm?: boolean
  Query?: string
  QuoteReliability?: number
  SecurityId?: number[]
  ShowRFQEligible?: boolean
  filter?: Filter
  isTicker?: boolean
  showLive?: boolean
  size?: number
  sortToTop?: number[]
  tickerAndMaturity?: boolean
}

export const getTypedArray = <T>() => [] as T[]
